/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { NextLocator } from '@grupo-constant/next-dtype/types';
import { BehaviorSubject, firstValueFrom, map, Observable, of } from 'rxjs';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { HttpService } from '../http/http.service';
import { CompanyUser, FlushUserNodePriviligesReply, FlushUserNodePriviligesRequest, SearchCompanyUserDNReply } from './companyUser.model';
import { UserUtils } from './user.utils';
import { NextBrand, UserNodePrivileges } from '../../modules/admin/projects/projects.types';
import { OUService } from '../../modules/admin/ou/ou.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private _user: BehaviorSubject<CompanyUser> = new BehaviorSubject<CompanyUser>(undefined);
    private _role: BehaviorSubject<UserNodePrivileges> = new BehaviorSubject(undefined);
    private _brands: BehaviorSubject<NextBrand[]> = new BehaviorSubject(undefined);

    constructor(
        private _httpService: HttpService,
        private ouService: OUService,
        private usersUtils: UserUtils
    ) {
        localStorage.removeItem(StorageKeysEnum.role);
    }

    get user$(): Observable<CompanyUser> {
        return this._user.asObservable();
    }

    get role$(): Observable<UserNodePrivileges> {
        return this._role.asObservable();
    }

    get brands$(): Observable<NextBrand[]> {
        return this._brands.asObservable();
    }

    set user(value: CompanyUser) {
        localStorage.setItem(StorageKeysEnum.user, JSON.stringify(value));
        // Store the value
        this._user.next(value);
    }

    set role(value: UserNodePrivileges) {
        // Store the value
        this._role.next(value);
    }

    set brands(value: NextBrand[]) {
        localStorage.setItem(StorageKeysEnum.userBrands, JSON.stringify(value));
        // Store the value
        this._brands.next(value);
    }

    /**
     * Get the current logged in user data
     */
    get(): Observable<CompanyUser> {
        let user = this._user.getValue();
        if (!user) {
            user = JSON.parse(localStorage.getItem(StorageKeysEnum.user)) as CompanyUser;
            this.user = user;
        }
        if (!user?.profileImage) {
            this.usersUtils.getRandomAnimalAvatar().then((image) => {
                user.profileImage = image;
                firstValueFrom(this.update(user)).then((newUser) => {
                    if (!!newUser) {
                        this.user = newUser;
                        return of(newUser);
                    } else {
                        return of(user);
                    }
                });
            });
        }
        return of(user);
    }

    getRole(): Observable<UserNodePrivileges> {
        const role = this._role.getValue();
        if (!role) {
            return this.ouService.getOu().pipe(map((ou) => {
                this.role = ou.role;
                return ou.role;
            }));
        }
        return of(role);
    }

    getBrands(): Observable<NextBrand[]> {
        let brands = this._brands.getValue();
        if (!brands) {
            brands = JSON.parse(localStorage.getItem(StorageKeysEnum.userBrands)) as NextBrand[];
            this.brands = brands ?? [];
        }
        return of(brands);
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(companyUser: CompanyUser): Observable<CompanyUser> {
        return this._httpService.post<any>(NextMethodsEnum.flushCompanyUser, { companyUser }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    this._user.next(response.companyUser);
                    localStorage.setItem(StorageKeysEnum.user, JSON.stringify(response.companyUser));
                    return response.companyUser;
                }
                return undefined;
            })
        );
    }

    delete() {
        localStorage.removeItem(StorageKeysEnum.user);
        localStorage.removeItem(StorageKeysEnum.userBrands);
    }

    retrieveCompanyUser(companyId: string, orderBy?: string) {
        return this._httpService.post(NextMethodsEnum.retrieveCompanyUserList, {
            companyId,
            orderBy,
            chunkSize: 0
        }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    return response.companyUser;
                }
                return [];
            })
        );
    }

    /**
     * Search users from a company
     *
     * @param expr
     * @param companyId
     */
    searchCompanyUser(expr: string, companyId: string): Observable<CompanyUser[]> {
        return this._httpService.post(NextMethodsEnum.searchCompanyUserList, {
            expr,
            company: companyId
        }).pipe(
            map((response) => {
                if (response && response.companyUser) {
                    return response.companyUser.filter(user => user.active);
                }
                return [];
            })
        );
    }

    searchCompanyUserDN(locator: NextLocator, expr: string, planningArea: string | string[]): Observable<SearchCompanyUserDNReply> {
        return this._httpService.post<SearchCompanyUserDNReply>(NextMethodsEnum.searchCompanyUserDN, {
            locator,
            expr,
            planningArea
        }).pipe(
            map(response => response ?? { companyUser: [], zombie: [], alias: [] })
        );
    }

    searchMissionUserListByBrand(marca_code: string, activeOnly: boolean, expr: string): Observable<any> {
        return this._httpService.post(NextMethodsEnum.searchMissionUserListByBrand, {
            marca_code,
            activeOnly,
            expr
        }).pipe(
            map(response => response.companyUser ?? {})
        );
    }

    getUserAvatarUrl(user?: CompanyUser): Observable<string> {
        if (!user) {
            return this._user.pipe(
                map(_user => `${this._httpService.getBaseUrl()}${_user.profileImage}`)
            );
        } else {
            return of(`${this._httpService.getBaseUrl()}${user.profileImage}`);
        }
    }

    flushNodePrivileges(request: FlushUserNodePriviligesRequest) {
        return this._httpService.post<FlushUserNodePriviligesReply>(NextMethodsEnum.flushUserNodePrivileges, request);
    }
}
